import type { AffixId, DieSides } from '../../../core/types';
import type { MaterialId } from '../../../data/materials';
import {
  DEFAULT_DICE_VIEW,
  DICE_SORT_LABEL,
  affixHasThreshold,
  choosableAffixes,
  sortKeysFor,
  ENRICHED_LABEL,
  isDefaultView,
  materialName,
  readDiceView,
  readDiceViewOpen,
  viewSummary,
  writeDiceView,
  writeDiceViewOpen,
  type DiceView,
  type EnrichedFilter,
} from '../../diceView';

/**
 * Panneau « Tri et filtres » des dés (v0.55.8, A119) — le **même composant**
 * pour l'Inventaire, le Recycleur, les cibles de la Forge et la fenêtre
 * « Équiper l'emplacement N » : un seul état de vue (`ui/diceView.ts`), mémorisé
 * pour la session, replié par défaut.
 */

export interface DiceViewPanelOptions {
  /** Appelé après chaque changement (la vue est déjà écrite) : l'écran se reconstruit. */
  onChange(): void;
  /** Matériaux proposés au filtre (ceux des dés de l'inventaire). */
  materials: MaterialId[];
  /** Faces proposées au filtre (celles des dés de l'inventaire). */
  sides: DieSides[];
}

function option(value: string, label: string, selected: boolean): HTMLOptionElement {
  const opt = document.createElement('option');
  opt.value = value;
  opt.textContent = label;
  opt.selected = selected;
  return opt;
}

function field(label: string, role: string, options: [string, string][], current: string): HTMLLabelElement {
  const wrap = document.createElement('label');
  wrap.className = 'dice-view-field';
  const text = document.createElement('span');
  text.className = 'dice-view-label';
  text.textContent = label;
  const select = document.createElement('select');
  select.className = 'f-select';
  select.setAttribute('data-role', role);
  select.setAttribute('aria-label', label);
  for (const [value, text2] of options) select.appendChild(option(value, text2, value === current));
  wrap.append(text, select);
  return wrap;
}

export function buildDiceViewPanel(opts: DiceViewPanelOptions): HTMLElement {
  const view = readDiceView();

  const details = document.createElement('details');
  details.className = 'f-panel f-details dice-view';
  details.setAttribute('data-role', 'dice-view');
  details.open = readDiceViewOpen();
  details.addEventListener('toggle', () => {
    if (details.open !== readDiceViewOpen()) writeDiceViewOpen(details.open);
  });

  const summary = document.createElement('summary');
  summary.innerHTML = `<span class="dice-view-title">Tri et filtres</span><span class="dice-view-summary" data-role="dice-view-summary"></span>`;
  (summary.querySelector('[data-role="dice-view-summary"]') as HTMLElement).textContent = viewSummary(view);
  details.appendChild(summary);

  // Les valeurs en cours restent proposées même si plus aucun dé ne les porte (la vue reste lisible).
  const materials = opts.materials.includes(view.material as MaterialId) || view.material === 'all'
    ? opts.materials
    : [...opts.materials, view.material as MaterialId];
  const sides = opts.sides.includes(view.sides as DieSides) || view.sides === 'all'
    ? opts.sides
    : [...opts.sides, view.sides as DieSides];

  const grid = document.createElement('div');
  grid.className = 'dice-view-grid';
  grid.append(
    field('Tri', 'view-sort', sortKeysFor(view).map((k) => [k, DICE_SORT_LABEL[k]] as [string, string]), view.sort),
    field(
      'Enrichi',
      'view-enriched',
      (['all', 'yes', 'no'] as EnrichedFilter[]).map((k) => [k, ENRICHED_LABEL[k]] as [string, string]),
      view.enriched,
    ),
    field(
      'Matériau',
      'view-material',
      [['all', 'Tous'], ...materials.map((m) => [m, materialName(m)] as [string, string])],
      view.material,
    ),
    field(
      'Faces',
      'view-sides',
      [['all', 'Toutes'], ...sides.map((n) => [String(n), `d${n}`] as [string, string])],
      String(view.sides),
    ),
    field('Affixe', 'view-affix', [['all', 'Tous'], ...choosableAffixes().map((a) => [a.id, a.name] as [string, string])], view.affix),
  );
  // A122-13 : « ≥ X % » seulement pour un affixe à plage (entier de 0 à 200).
  if (view.affix !== 'all' && affixHasThreshold(view.affix)) {
    const wrap = document.createElement('label');
    wrap.className = 'dice-view-field dice-view-field--min';
    const text = document.createElement('span');
    text.className = 'dice-view-label';
    text.textContent = '≥ X %';
    const input = document.createElement('input');
    input.type = 'number';
    input.className = 'f-input dice-view-min';
    input.min = '0';
    input.max = '200';
    input.step = '1';
    input.inputMode = 'numeric';
    input.value = String(Math.round(view.affixMin * 100));
    input.setAttribute('data-role', 'view-affix-min');
    input.setAttribute('aria-label', 'Somme minimale de l’affixe, en pourcent');
    wrap.append(text, input);
    grid.appendChild(wrap);
  }
  details.appendChild(grid);

  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'f-btn f-btn--ghost f-btn--sm dice-view-reset';
  reset.setAttribute('data-action', 'view-reset');
  reset.textContent = 'Réinitialiser';
  reset.disabled = isDefaultView(view);
  details.appendChild(reset);

  const read = (): DiceView => {
    const q = (role: string): string => (details.querySelector(`[data-role="${role}"]`) as HTMLSelectElement).value;
    const sidesValue = q('view-sides');
    const affix = q('view-affix') as DiceView['affix'];
    const minEl = details.querySelector<HTMLInputElement>('[data-role="view-affix-min"]');
    // Le seuil ne survit qu'à un affixe inchangé : en changer repart de 0 (le champ n'a peut-être pas de sens).
    const raw = minEl && affix === view.affix ? Math.round(Number(minEl.value)) : 0;
    const affixMin = affix !== 'all' && affixHasThreshold(affix as AffixId) && Number.isFinite(raw) ? Math.min(200, Math.max(0, raw)) / 100 : 0;
    // Le tri « Affixe » n'existe qu'avec un affixe choisi.
    const sort = q('view-sort') as DiceView['sort'];
    return {
      affix,
      affixMin,
      sort: sort === 'affix' && affix === 'all' ? 'level' : sort,
      enriched: q('view-enriched') as EnrichedFilter,
      material: q('view-material') as DiceView['material'],
      sides: sidesValue === 'all' ? 'all' : (Number(sidesValue) as DieSides),
    };
  };
  // L'événement `toggle` arrive en différé : l'état d'ouverture est aussi relevé ici, avant la
  // reconstruction de l'écran, pour que le panneau qu'on vient d'ouvrir ne se referme pas.
  grid.addEventListener('change', () => {
    writeDiceViewOpen(details.open);
    writeDiceView(read());
    opts.onChange();
  });
  reset.addEventListener('click', () => {
    writeDiceViewOpen(details.open);
    writeDiceView({ ...DEFAULT_DICE_VIEW });
    opts.onChange();
  });
  return details;
}

/**
 * Ligne « N dés masqués par les filtres — Tout afficher » : rend `null` quand
 * rien n'est masqué (aucun filtre, ou le filtre ne cache aucun dé). Une vue
 * filtrante ne cache jamais l'existence des dés.
 */
export function buildHiddenNotice(hidden: number, onChange: () => void): HTMLElement | null {
  if (hidden <= 0) return null;
  const p = document.createElement('p');
  p.className = 'dice-view-hidden';
  p.setAttribute('data-role', 'view-hidden');
  const text = document.createElement('span');
  text.textContent = `${hidden} dé${hidden > 1 ? 's' : ''} masqué${hidden > 1 ? 's' : ''} par les filtres — `;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'dice-view-showall';
  btn.setAttribute('data-action', 'view-reset');
  btn.textContent = 'Tout afficher';
  btn.addEventListener('click', () => {
    writeDiceView({ ...DEFAULT_DICE_VIEW });
    onChange();
  });
  p.append(text, btn);
  return p;
}
